import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '@/lib/ConfigProvider'
import { ApiError } from '@/lib/api'
import { startVerification } from '@/onboarding/onboarding'
import { appOrigin } from '@/lib/origin'
import { useSession } from '@/auth/SessionProvider'
import { useOnboarding } from '@/onboarding/OnboardingProvider'
import { Register } from '@/onboarding/Register'
import { evidenceLabel, STATE_LABEL } from '@/onboarding/onboarding'
import type { Onboarding, OnboardingState } from '@/onboarding/onboarding'
import './Welcome.css'

/**
 * Refusals the customer cannot retry their way out of (`go-wallet#399`, `#416`).
 *
 * A SET, not a chain of equality tests. The first version of this branch tested
 * one code with everything else falling through to "offer a retry", and the
 * very next contract change added a 409 carrying the identical instruction —
 * which then got the retry (#75). The default is right, and it is the shape
 * that was wrong.
 *
 *   verification_blocked      502. The vendor refused THIS deployment, or gave
 *                             us a session we cannot use. An operator must act.
 *   already_decided           409. The application is decided.
 *   verification_in_progress  409. Documents are submitted and being judged.
 *   idempotency_conflict      409. The key belongs to somebody else — ours to
 *                             fix, not theirs.
 *
 * The contract is directive about all of them: "a client MUST NOT offer one".
 */
const NO_RETRY = new Set([
  'verification_blocked',
  'already_decided',
  'verification_in_progress',
  'idempotency_conflict',
])

/**
 * What to say after a refusal the customer cannot retry.
 *
 * # This used to be a boolean, and the boolean was a lie half the time (#97)
 *
 * A `REASSURING` set held `already_decided` alongside `verification_in_progress`
 * and rendered both as *"There is nothing for you to do. We will let you know as
 * soon as there is news."*
 *
 * `verification_in_progress` earns that sentence. Documents are submitted and
 * being judged; news really is coming.
 *
 * `already_decided` does not, because the backend returns that ONE code for BOTH
 * terminal states. `internal/server/onboarding.go` guards on
 * `onboarding.IsTerminal(c.Acceptance)`, and `IsTerminal` is true of `accepted`
 * AND of `rejected` — the body says a decision exists and never which one. So a
 * customer whose application had been REFUSED pressed the button and was told to
 * sit tight and wait for news that was never coming. It is the most
 * consequential sentence this screen can produce, and it was addressed to
 * precisely the person it is wrong for.
 *
 * # So it defers to the read the page has already made
 *
 * `Status` holds the onboarding, which knows the verdict. Where that read
 * predates the decision — the refusal is itself proof one landed — this says so
 * rather than picking the comfortable half. "We cannot see which" is an honest
 * answer and the fourth one, exactly as in #54 and #70.
 */
function blockedNote(code: string, o: Onboarding): { text: string; stale: boolean } {
  if (code === 'verification_in_progress') {
    return {
      text: 'There is nothing for you to do. We will let you know as soon as there is news.',
      stale: false,
    }
  }

  if (code === 'already_decided') {
    if (o.state === 'accepted') {
      return { text: 'Your application has been accepted and your wallet is open.', stale: false }
    }
    if (o.state === 'rejected') {
      // No promise of news, and no invitation to do anything. Both would be
      // false, and the second would be cruel.
      return {
        text: 'The institution could not accept this application, so there is nothing further to do here.',
        stale: false,
      }
    }
    return {
      text: 'A decision has been made on your application, and this screen has not caught up with it yet.',
      stale: true,
    }
  }

  return {
    text: 'This is something we have to fix at our end — there is nothing for you to do, and trying again will not change it. We are on it.',
    stale: false,
  }
}

/**
 * The journey between having an account and being able to use the wallet.
 *
 * Registration creates an Ory identity. It does **not** create permission to
 * hold or move money — that is onboarding, and it is fail-closed (invariant 7)
 * and decided by the institution, never by us and certainly never here. This
 * screen exists so the gap between the two is visible rather than experienced
 * as a wallet that mysteriously refuses everything.
 */
/**
 * The opening paragraph, which is this app's own framing and not the backend's
 * wording (#146).
 *
 * It read, in every state:
 *
 *   "Your account is ready. Before you can hold or move money, the institution
 *    we work with has to verify who you are. That is a legal requirement, not a
 *    formality — and it is their decision, not ours."
 *
 * Correct for `pending`, `in_review` and `more_info_required`. To somebody
 * already accepted it described work that is finished, in the present tense,
 * directly above four ticks, a `Verified` chip and the backend's own sentence
 * saying the wallet is open. Nothing could reach `accepted` on any deployment
 * until `go-wallet#423`, so the screen had been correct in every state anybody
 * could render.
 *
 * # What the accepted wording deliberately does NOT say
 *
 * It does not promise that money can now be held or moved. Every money-adjacent
 * instruction still goes through `authz.Authorize` server-side (invariant 8),
 * an account carries its own `can_receive` and `can_cash_out`, and a screen
 * that turned "verified" into "you can now do X" would be this app making an
 * authorization claim it is not entitled to make. It says what is true of the
 * checks and stops.
 *
 * It also does not restate the backend's `blocking` sentence, which `Status`
 * renders verbatim a few lines below. One wording per fact.
 */
function lede(accepted: boolean): string {
  return accepted
    ? 'Your account is ready and the institution has verified who you are. There is nothing left for you to do here.'
    : 'Your account is ready. Before you can hold or move money, the institution we work with has to verify who you are. That is a legal requirement, not a formality — and it is their decision, not ours.'
}

export function Welcome() {
  const { state: session, logout } = useSession()
  const { state, refresh } = useOnboarding()
  const firstName =
    session.status === 'authenticated' ? session.identity.label.split(/[\s@]/)[0] : null
  // Read once and reused, so the lede, the CTA and the escape hatch cannot
  // disagree about which state this screen is in (#146).
  const accepted = state.status === 'ready' && state.onboarding.state === 'accepted'

  return (
    <main className="welcome">
      <div className="welcome__panel">
        <span className="welcome__mark">TESORA</span>
        <h1 className="welcome__title">
          {firstName === null ? 'Welcome' : `Welcome, ${firstName}`}
        </h1>
        <p className="welcome__lede">{lede(accepted)}</p>

        <ol className="steps">
          <Step n={1} done label="Create your account" note="Done." />
          <Step
            n={2}
            label="Open your wallet"
            note={openWalletNote(state)}
            done={state.status === 'ready'}
            current={state.status === 'unregistered'}
          />
          <Step
            n={3}
            label="Verify your identity"
            note={verifyIdentityNote(state)}
            current={state.status === 'ready' && state.onboarding.state !== 'accepted'}
            done={state.status === 'ready' && state.onboarding.state === 'accepted'}
          />
          <Step
            n={4}
            label="Start using Tesora"
            note={startUsingNote(state)}
            done={state.status === 'ready' && state.onboarding.state === 'accepted'}
          />
        </ol>

        {state.status === 'loading' && <p className="welcome__muted">Checking where you stand…</p>}

        {/*
          Signed in, and not a customer yet — the state every person who has ever
          signed up passes through. `POST /v1/registrations` is what leaves it,
          and until #22 nothing in this app called it, so the journey stopped
          here for everybody (`go-wallet#292`: "Registration was half-built and
          the missing half was ours").
        */}
        {state.status === 'unregistered' && <Register onRegistered={refresh} />}

        {state.status === 'unavailable' && (
          <div className="welcome__notice" role="status">
            <p className="welcome__noticeTitle">We cannot tell you where your checks stand.</p>
            <p className="welcome__muted">{state.reason}</p>
            {/*
              Deliberately NOT "you have not started yet". We do not know that.
              Telling a customer to redo checks they have already completed is
              worse than telling them we cannot see.
            */}
            <button type="button" className="welcome__ghost" onClick={() => void refresh()}>
              Check again
            </button>
          </div>
        )}

        {state.status === 'ready' && (
          <Status onboarding={state.onboarding} onRefresh={() => void refresh()} />
        )}

        {/*
          A PLAIN WAY IN, FOR SOMEBODY WHO HAS BEEN ACCEPTED (#123).

          Until now the only exit from this screen was "Go to the wallet
          anyway" -- and `/welcome` sits outside the shell, so there is no rail
          either. A customer whose application is accepted, whose wallet is
          open, and who has been told so two lines above was reading the sole
          way onward as an override of a refusal that is not happening.

          It goes to `/` and not to a specific screen: which screen an accepted
          customer lands on is #120's question, and this should not answer it
          twice.
        */}
        {accepted && (
          // "Go to" rather than "Open" (#146). Step 2 above is labelled "Open
          // your wallet" and reads "Done." — the wallet is the thing that was
          // opened, three lines up. The same words for "this already happened"
          // and "go here now" made the button look like an action that was
          // still outstanding.
          <Link className="welcome__cta" to="/">
            Go to your wallet
          </Link>
        )}

        <nav className="welcome__links">
          {/*
            THE LINK STAYS IN EVERY STATE. ONLY ITS LABEL FOLLOWS THE STATE (#146).

            "Anyway" is right for somebody the routing default would divert:
            `?anyway=1` reaches the wallet PAST that default (#81), it works from
            any state including one we cannot read, and #86 records it as a
            property kept on purpose. For an accepted customer nothing is being
            overridden, so the word says the app is refusing something it is not
            — the reading #123 removed from the CTA and left standing here.

            TWO DRAFTS OF THIS WERE WRONG AND A BROWSER SHOWED BOTH.

            The first hid the link when accepted. Rendering the screen showed why
            that is not safe: the CTA above goes to `/`, and `WalletHome` READS
            ONBOARDING AGAIN there. If that second read fails it routes
            `unavailable` back to this screen, so an accepted customer can bounce
            off their own wallet. `?anyway=1` cannot bounce — it is
            unconditional. The two destinations are not equivalent, which is the
            substantive reason to keep the link and a better one than the copy
            argument I started with.

            The second draft labelled it "Go to the wallet" beside a CTA reading
            "Go to your wallet" — two controls, near-identical words, and no way
            to tell which was which. The label below says what the link actually
            does, which is the thing that distinguishes it.
          */}
          <Link to="/?anyway=1">
            {accepted ? 'Go to the wallet without checking again' : 'Go to the wallet anyway'}
          </Link>
          <button type="button" className="welcome__linkish" onClick={() => void logout()}>
            Sign out
          </button>
        </nav>
        {/*
          "Go to the wallet anyway" is not a loophole, and `?anyway=1` is what
          makes it work now that `/` defaults a non-onboarded customer to this
          screen (#81). It is unconditional: it reaches the wallet from any
          state, including one we cannot read. The frontend does not
          enforce onboarding — the backend refuses every money-adjacent
          instruction through authz.Authorize regardless of what this app
          renders (invariant 8). A client-side block would be an authorization
          decision in a browser bundle, and would also strand anyone whose state
          we simply cannot read.
        */}
      </div>
    </main>
  )
}

function Status({ onboarding, onRefresh }: { onboarding: Onboarding; onRefresh: () => void }) {
  const outstanding = onboarding.evidence.filter((e) => e.status !== 'verified')

  return (
    <div className="welcome__status">
      <p className="welcome__state">
        <span className={`statuschip statuschip--${onboarding.state}`}>{STATE_LABEL[onboarding.state]}</span>
        <span className="welcome__muted">
          {onboarding.waitingOn === 'customer'
            ? 'Waiting on you'
            : onboarding.waitingOn === 'institution'
              ? 'Waiting on the institution — nothing for you to do'
              : ''}
        </span>
      </p>

      {onboarding.blocking !== '' && <p className="welcome__muted">{onboarding.blocking}</p>}

      {outstanding.length > 0 && (
        <ul className="welcome__evidence">
          {outstanding.map((e) => (
            <li key={e.type}>
              <span>{evidenceLabel(e.type)}</span>
              <span className={`statuschip statuschip--${e.status}`}>{e.status}</span>
            </li>
          ))}
        </ul>
      )}

      <StartVerification onboarding={onboarding} onRefresh={onRefresh} />
    </div>
  )
}

/**
 * The one action on this screen.
 *
 * The customer is sent to the vendor's hosted flow, captures documents there,
 * and the vendor webhooks the verdict to the backend. **Coming back proves
 * nothing** — they return the instant they finish uploading, long before a
 * verdict exists — so `redirect_url` lands them here and this screen re-reads
 * backend state rather than declaring success.
 */
function StartVerification({
  onboarding,
  onRefresh,
}: {
  onboarding: Onboarding
  onRefresh: () => void
}) {
  const api = useApi()
  const [busy, setBusy] = useState(false)
  /**
   * The refusal, carrying the CODE so the screen can offer the right control.
   *
   * A bare string could only be printed. `second_factor_required` needs a route
   * to the settings screen instead of a retry, and telling the two apart is the
   * code's job.
   */
  const [error, setError] = useState<{ readonly message: string; readonly code: string } | null>(
    null,
  )
  /**
   * A refusal the customer cannot do anything about (`go-wallet#399`).
   *
   * `502 verification_blocked` means the vendor refused THIS DEPLOYMENT — a
   * rejected credential, an exhausted account limit, a workflow it will not
   * accept — and an operator has to act. The contract is directive about what a
   * client does with it: "a client MUST NOT offer a retry affordance. Branch on
   * the error code, not on the status."
   *
   * Separate state rather than a flag on `error`, because the two are opposite
   * instructions to the customer and the screen must not be able to show both.
   */
  const [blocked, setBlocked] = useState<{
    message: string
    decisionRef?: string
    // The CODE, not a pre-computed tone. `already_decided` means two opposite
    // things and only the onboarding state can tell them apart, so the decision
    // about what to say belongs at render, next to that state (#97).
    code: string
  } | null>(null)
  // One key per mounted screen, so a double-click is one intent and one vendor
  // session (invariant 5) — not two, which would be two document checks for one
  // person.
  const [key] = useState(() => `verify-${crypto.randomUUID()}`)

  // RECOVER FROM THE BACK BUTTON (#56).
  //
  // `go` deliberately leaves `busy` set after `location.assign` — the page is
  // leaving, and clearing it would flash the button back to life mid-navigation.
  // That is right for the ordinary path and wrong for one specific return.
  //
  // Navigating to the vendor makes this page eligible for the back/forward
  // cache, which freezes the whole page INCLUDING the JS heap and restores it
  // exactly. So a customer who opens the vendor, thinks better of it and presses
  // Back lands here with `busy === true` restored: the only control on the
  // screen reads "Opening…" and is disabled, with nothing to clear it. No
  // remount happens, because avoiding the remount is the entire point of
  // bfcache. Their only way out is to reload a page that gives no sign it needs
  // reloading.
  //
  // The ordinary return hides this completely: finishing at the vendor comes
  // back through `redirect_url`, which is a fresh navigation, so the component
  // remounts and `busy` starts false. The working path and the broken path
  // differ only by how the customer came back — and the broken one belongs to
  // anybody who has second thoughts about photographing their passport, which is
  // not a rare customer.
  //
  // `persisted` is the only signal that distinguishes a restore from a load.
  //
  // Placed above the early returns below: a hook after a conditional return
  // makes hook order depend on state, which React forbids.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setBusy(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  // A TERMINAL VERDICT OUTRANKS A LIVE SESSION URL (#123).
  //
  // Scoped to THIS branch and not to the whole component: `#97` tests that a
  // decided application still renders "Verify my identity" and answers the
  // press with `already_decided`, which is how the screen learns a verdict it
  // was not told about. Returning null here would delete that path.
  //
  // This early return used to come first and looked at no state at all, so an
  // accepted customer could be offered "Continue verifying my identity". The
  // backend sets `can_start_verification` false once acceptance is terminal,
  // but it computes `verification_url` on a DIFFERENT question -- whether the
  // session is worth resuming -- and the two are deliberately independent:
  // "a session can stop being resumable without becoming restartable"
  // (internal/server/onboarding.go).
  //
  // So the frontend was relying on the backend never sending a combination the
  // backend's own comment says it may. go-wallet#423 is exactly the shape that
  // produces it: it advances the institution's answer and is explicitly scoped
  // to NOT touch the identity verdict, so acceptance can land while the session
  // is still `pending` or `retry`.
  // A session already running: continue it rather than starting a second --
  // UNLESS the application has been decided.
  const terminal = onboarding.state === 'accepted' || onboarding.state === 'rejected'
  const live = terminal ? undefined : onboarding.verificationUrl
  if (live !== undefined) {
    return (
      <a className="welcome__cta" href={live}>
        Continue verifying my identity
      </a>
    )
  }

  if (!onboarding.canStartVerification) return null

  const go = async () => {
    setBusy(true)
    setError(null)
    try {
      const url = await startVerification(api, key, `${appOrigin()}/welcome`)
      window.location.assign(url)
    } catch (e) {
      setBusy(false)
      // Branch on the CODE, not the status, as the contract says. A code this
      // bundle does not recognise falls through to the retry, because
      // "transient" is the safe default when we cannot tell: offering a retry
      // that fails wastes a press, and withholding one that would have worked
      // strands somebody who could have got through.
      if (e instanceof ApiError && NO_RETRY.has(e.code)) {
        setBlocked({
          message: e.message,
          // Invariant 8, and api.ts says it in the type: "Show it; never
          // swallow it." A customer on the phone to support with no reference
          // is why that rule exists.
          ...(e.decisionRef === undefined ? {} : { decisionRef: e.decisionRef }),
          code: e.code,
        })
        return
      }
      // THE CODE, not only the sentence.
      //
      // `second_factor_required` is a refusal the customer can clear
      // themselves, and clearing it happens on a DIFFERENT screen. Rendering
      // the message alone told somebody to turn on two-factor authentication
      // and gave them nowhere to do it, beside a retry that cannot help — the
      // same "read the state correctly, then offer an action that cannot work"
      // shape this file already names twice (go-wallet#1025).
      setError({
        message: e instanceof Error ? e.message : 'Could not start verification.',
        code: e instanceof ApiError ? e.code : '',
      })
    }
  }

  // No control at all. Every press would produce another vendor call, another
  // 502 and the same sentence — which is the #17 and #22 shape a third time:
  // reading the state correctly and then offering an action that cannot help.
  if (blocked !== null) {
    const note = blockedNote(blocked.code, onboarding)
    return (
      <div className="welcome__notice" role="alert">
        <p className="welcome__noticeTitle">{blocked.message}</p>
        <p className="welcome__muted">{note.text}</p>
        {/*
          Offered only where the screen has admitted it is behind, and it is the
          same control the `unavailable` branch offers for the same reason: the
          honest answer is "we cannot see it from here", and the customer should
          not have to work out that reloading is what fixes that.
        */}
        {note.stale && (
          <button type="button" className="welcome__ghost" onClick={() => void onRefresh()}>
            Check again
          </button>
        )}
        {blocked.decisionRef !== undefined && (
          <p className="welcome__muted">
            Reference: <code>{blocked.decisionRef}</code>
          </p>
        )}
      </div>
    )
  }

  return (
    <>
      {error !== null && (
        <p className="welcome__muted" role="alert">
          {error.message}
        </p>
      )}
      {/*
        A ROUTE, not a retry.
        `second_factor_required` is the one refusal here that the customer
        clears on another screen. Pressing "Verify my identity" again produces
        the identical refusal, so the second factor is offered FIRST and the
        retry stays available beneath it for somebody who has already enrolled
        in another tab.
        The wording names no method. Which factor to enrol is the identity
        provider's to offer — the backend refuses with the assurance LEVEL and
        never the method, and a link that promised an authenticator app would
        be wrong the day a passkey is switched on.
      */}
      {error?.code === 'second_factor_required' && (
        <Link className="welcome__cta" to="/settings">
          Set up two-factor authentication
        </Link>
      )}
      <button type="button" className="welcome__cta" disabled={busy} onClick={() => void go()}>
        {busy ? 'Opening…' : 'Verify my identity'}
      </button>
    </>
  )
}

/**
 * Step 2's note, as an EXHAUSTIVE switch rather than a chain of ternaries.
 *
 * The chain it replaces named three of the four states and let the fourth fall
 * into the `else`, which returned 'Done.'. So a customer whose onboarding could
 * not be READ was told their wallet was open — on the same screen, directly
 * above "We cannot tell you where your checks stand." The step was not even
 * ticked, because `done` is keyed on `ready`; it rendered an unticked step whose
 * note said it was finished.
 *
 * `onboarding.ts` opens by warning about exactly this: "we have not started your
 * checks" and "we cannot tell you where your checks stand" look identical in a
 * status list and mean opposite things about what the customer should do next.
 * This was the narrower and worse version — not a wrong cause narrated, but a
 * step asserted complete on the strength of not having been able to look.
 *
 * The switch is the fix, not the wording. A fifth state is now a compile error
 * here instead of a silent 'Done.', which is the same bargain `fetchOnboarding`
 * makes when it refuses a backend state it does not recognise rather than
 * defaulting it.
 */
/**
 * Step 3's note. Exhaustive, for the reason #54 made expensive one line above
 * this one (#70).
 *
 * The ternary it replaces sent every non-`ready` state — including
 * `unavailable` — to "You will be taken to our identity provider to photograph
 * a document." So a customer whose read failed, who may well have verified
 * already, was told what would happen next on the strength of our not having
 * been able to look. Milder than the "Done." it sat beneath, being a prediction
 * rather than a claim of completion, and the same mistake.
 *
 * #54 was fixed with a switch precisely so a fifth state could not fall
 * through, and then the very next Step kept its ternary. That is why this one
 * is a switch too.
 */
function verifyIdentityNote(state: OnboardingState): string {
  switch (state.status) {
    case 'ready':
      return evidenceNote(state.onboarding)
    case 'loading':
      return 'Checking…'
    case 'unregistered':
      return 'You will be taken to our identity provider to photograph a document.'
    case 'unavailable':
      // We cannot see their state, so we do not tell them what happens next.
      // The notice below says why, once, rather than each step guessing.
      return 'We could not check.'
  }
}

/**
 * Step 4's note, derived from the state its own tick is keyed to (`#123`).
 *
 * This was a hardcoded constant while steps 2 and 3 got exhaustive switches --
 * `#54` and `#70` were each filed for a step whose note did not follow its
 * state, and this is the same defect one line further down. Ticked and still
 * reading "Once the institution accepts you, the wallet opens" is a completed
 * step describing the condition for completing it.
 *
 * `rejected` says what is true without saying what was decided: the verdict
 * belongs to the notice below, which has the whole state to work with, and a
 * step note repeating it says it worse in less room.
 */
function startUsingNote(state: OnboardingState): string {
  switch (state.status) {
    case 'ready':
      switch (state.onboarding.state) {
        case 'accepted':
          return 'Your wallet is open.'
        case 'rejected':
          return 'This is not open to you.'
        default:
          return 'Once the institution accepts you, the wallet opens.'
      }
    case 'loading':
      return 'Checking…'
    case 'unregistered':
      return 'Once the institution accepts you, the wallet opens.'
    case 'unavailable':
      // Same rule as the other two: we cannot see their state, so we do not
      // tell them what happens next.
      return 'We could not check.'
  }
}

function openWalletNote(state: OnboardingState): string {
  switch (state.status) {
    case 'unregistered':
      return 'We need your country and your legal name.'
    case 'loading':
      return 'Checking…'
    case 'ready':
      return 'Done.'
    case 'unavailable':
      // Deliberately not "Done." and not "Not started". We do not know which,
      // and the notice below says so in full.
      return 'We could not check.'
  }
}

function Step({
  n,
  label,
  note,
  done = false,
  current = false,
}: {
  n: number
  label: string
  note: string
  done?: boolean
  current?: boolean
}) {
  return (
    <li className="step" data-done={done} data-current={current && !done}>
      <span className="step__n" aria-hidden="true">
        {done ? '✓' : n}
      </span>
      <span className="step__body">
        <span className="step__label">{label}</span>
        <span className="step__note">{note}</span>
      </span>
    </li>
  )
}

/**
 * What to say about the identity step, given only what the evidence list proves.
 *
 * # The empty list is the whole problem
 *
 * This used to filter for unverified items and, finding none, say "Everything is
 * submitted." An **empty** list has none either — and evidence rows are written
 * only when the identity vendor reports a verdict, so an empty list is the state
 * every customer is in from the moment they register until they have been
 * through the vendor.
 *
 * So the first thing a new customer read, under the heading "Verify your
 * identity", was that everything was submitted — beside a chip saying "Not
 * started", above a button offering to start, and directly above the backend's
 * own sentence saying we still need to verify them. One screen, four claims,
 * three of them contradicting the one that was wrong (#27).
 *
 * It became reachable the day `go-wallet#287` shipped: before that, onboarding
 * was always `unavailable` and this function never ran against real data.
 *
 * # Why it does not key off canStartVerification
 *
 * That was the obvious fix and it is wrong. `can_start_verification` is false
 * whenever no identity vendor covers the customer's country, which also flips
 * `waiting_on` to the institution — so a freshly registered customer in an
 * uncovered country would be mislabelled again, just differently.
 *
 * The list says what has been *filed*. Nothing else here may claim more than it.
 */
function evidenceNote(o: Onboarding): string {
  if (o.state === 'accepted') return 'Done.'
  if (o.state === 'rejected') return 'The institution could not accept this application.'
  if (o.evidence.length === 0) {
    // A LIVE VENDOR SESSION IS EVIDENCE THAT IT STARTED (#103).
    //
    // The backend sets `verificationUrl` only while a session is open, and
    // `StartVerification` renders "Continue verifying my identity" from the same
    // field four lines below this note. Without this branch the ordinary return
    // path from the vendor — back before any per-document verdict has landed, so
    // `evidence` is still empty — put "Not started yet." directly above a button
    // offering to continue the thing it said had not begun.
    //
    // Still not "submitted": a session that is open is one that is not finished.
    if (o.verificationUrl !== undefined) return 'In progress — nothing filed yet.'
    // Nothing filed. `pending` says so plainly; any other state means something
    // is under way but no per-document verdict has landed yet, and claiming
    // either "submitted" or "not started" would be guessing.
    return o.state === 'pending' ? 'Not started yet.' : 'Nothing to show yet.'
  }
  const outstanding = o.evidence.filter((e) => e.status !== 'verified')
  if (outstanding.length === 0) return 'Everything is submitted.'
  return `${outstanding.length} item${outstanding.length === 1 ? '' : 's'} outstanding.`
}
